import * as React from 'react'

import { SitePermitStatus } from '~/client/graph'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import * as Icons from '~/client/src/shared/components/Icons'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import { FILTER_TYPE_ICON_MAP } from '~/client/src/shared/constants/FilterTypeMappings'
import {
  getFormsStatusAsTag,
  getFormsStatusesAsTags,
} from '~/client/src/shared/constants/formStatusesTags'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import FileType, {
  AttachmentFileTypeList,
} from '~/client/src/shared/enums/FileType'
import {
  FilterType,
  getFilterTypeCaption,
  workflowFilterTypes,
} from '~/client/src/shared/enums/FilterType'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { ITag } from '~/client/src/shared/models/Tag'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'

import {
  FILE_TYPE_CATEGORY,
  IFilterTag,
  PROJECT_CATEGORY,
} from './WorkflowFilters.types'

import Colors from '~/client/src/shared/theme.module.scss'

/**
 * Filter item type that can be either a real ITag or a synthetic filter tag
 * (like file types or projects). This union type provides proper type safety
 * instead of using `as unknown as` casts.
 */
type FilterItem = ITag | IFilterTag

const UNASSIGNED_PREFIX = 'Unassigned_'

const getUnassignedId = (filterType: FilterType): string =>
  `${UNASSIGNED_PREFIX}${filterType}`

export const isUnassignedId = (tagId: string): boolean =>
  tagId.startsWith(UNASSIGNED_PREFIX)

export const getFilterTypeFromUnassignedId = (
  tagId: string,
): FilterType | null => {
  if (!isUnassignedId(tagId)) return null
  return tagId.replace(UNASSIGNED_PREFIX, '') as FilterType
}

const TAG_TYPES_WITH_BADGE: TagType[] = [TagType.Status]

const TAG_TYPES_WITH_SITEMAP_ICON: TagType[] = [
  TagType.Area,
  TagType.Building,
  TagType.Zone,
  TagType.Level,
  TagType.Gate,
  TagType.Route,
  TagType.OffloadingEquipment,
  TagType.InteriorDoor,
  TagType.InteriorPath,
  TagType.LogisticsObject,
  TagType.VerticalObject,
  TagType.Staging,
  TagType.Integration,
  TagType.ProjectLocation,
]

interface IUseWorkflowFilterCategoriesOptions {
  tagsStore?: TagsStore
  permitTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  formCategoriesStore?: WorkflowCategoriesStore
  projectsStore?: ProjectsStore
  availableProjectIds?: string[]
}

const getFileTypeTags = (): IFilterTag[] => {
  return AttachmentFileTypeList.map(fileType => ({
    id: `filetype_${fileType}`,
    name: fileType,
    type: FILE_TYPE_CATEGORY,
    color: '',
    iconName: null,
  }))
}

export function useWorkflowFilterCategories({
  tagsStore,
  permitTypesStore: workflowTypesStore,
  companiesStore,
  projectMembersStore,
  locationAttributesStore,
  formCategoriesStore: workflowCategoriesStore,
  projectsStore,
  availableProjectIds,
}: IUseWorkflowFilterCategoriesOptions): IDrillDownFilterCategory<FilterItem>[] {
  const getTagsForCategory = React.useCallback(
    (filterType: FilterType): FilterItem[] => {
      switch (filterType) {
        case FilterType.Category:
          return workflowCategoriesStore?.activeCategoriesAsTags || []

        case FilterType.Type:
          return workflowTypesStore?.actualTypesAsTags || []

        case FilterType.Status:
          return getFormsStatusesAsTags()

        case FilterType.Location:
          return locationAttributesStore?.allLocationsWithoutEquipment || []

        case FilterType.Equipment:
          return locationAttributesStore?.offloadingEquipmentsStore.list || []

        case FilterType.Company:
          return tagsStore?.tagListsByTagTypeMap[TagType.Company] || []

        case FilterType.ResponsibleContact:
          return tagsStore?.tagListsByTagTypeMap[TagType.User] || []

        case FilterType.Attachment:
          return getFileTypeTags()

        case FilterType.Project:
          if (!availableProjectIds?.length) return []
          return (
            projectsStore?.list
              .filter(project => availableProjectIds.includes(project.id))
              .map((project): IFilterTag => ({
                id: project.id,
                name: project.name,
                color: Colors.neutral60,
                iconName: null,
                type: PROJECT_CATEGORY,
              })) || []
          )

        default:
          return []
      }
    },
    [
      tagsStore,
      workflowTypesStore,
      locationAttributesStore,
      workflowCategoriesStore,
      projectsStore,
      availableProjectIds,
    ],
  )

  const getColoredTag = React.useCallback((tag: FilterItem): FilterItem => {
    if (tag.type === TagType.Status) {
      return getFormsStatusAsTag(tag.id as SitePermitStatus)
    }
    return tag
  }, [])

  const renderCustomIcon = React.useCallback(
    (tag: FilterItem): React.ReactNode => {
      switch (tag.type) {
        case TagType.PermitType: {
          const permitType = workflowTypesStore?.getPermitTypeById(tag.id)
          return (
            <WorkflowTypeIcon
              iconName={permitType?.iconName || 'general'}
              size="medium"
              className="no-grow mr8 icon-20"
            />
          )
        }

        case TagType.User: {
          const user = projectMembersStore?.getById(tag.id)
          const initials = user ? User.getInitialsToDisplay(user) : ''
          return (
            <Avatar
              src={user?.avatarUrl}
              initials={initials}
              size={AvatarSize.XMinuscule}
              avatarType={AvatarType.User}
              className="no-grow mr8"
            />
          )
        }

        case TagType.Company: {
          const company = companiesStore?.getCompanyById(tag.id)
          if (company?.avatarUrl || company?.logoUrl) {
            return (
              <Avatar
                src={company.avatarUrl || company.logoUrl}
                size={AvatarSize.XMinuscule}
                avatarType={AvatarType.Company}
                className="no-grow mr8"
              />
            )
          }
          return <Icons.LogoDark className="no-grow mr8 row icon-20" />
        }

        default: {
          if (tag.type === ('Project' as TagType)) {
            const project = projectsStore?.list.find(p => p.id === tag.id)
            if (project?.logoUrl) {
              return (
                <Avatar
                  src={project.logoUrl}
                  size={AvatarSize.XMinuscule}
                  avatarType={AvatarType.Company}
                  className="no-grow mr8"
                />
              )
            }
            return <Icons.LogoDark className="no-grow mr8 row icon-20" />
          }
          if ((tag.type as string) === FILE_TYPE_CATEGORY) {
            switch (tag.name) {
              case FileType.Image:
                return <Icons.FileImage className="no-grow mr8 row icon-20" />
              case FileType.Pdf:
                return <Icons.FilePdf className="no-grow mr8 row icon-20" />
              case FileType.Excel:
                return <Icons.FileExcel className="no-grow mr8 row icon-20" />
              case FileType.Word:
                return <Icons.FileWord className="no-grow mr8 row icon-20" />
              case FileType.PowerPoint:
                return (
                  <Icons.FilePowerpoint className="no-grow mr8 row icon-20" />
                )
              case FileType.Video:
                return <Icons.FileVideo className="no-grow mr8 row icon-20" />
              case FileType.OtherFile:
              default:
                return <Icons.File className="no-grow mr8 row icon-20" />
            }
          }
          return null
        }
      }
    },
    [workflowTypesStore, projectMembersStore, companiesStore, projectsStore],
  )

  const renderItemContent = React.useCallback(
    (tag: FilterItem): React.ReactNode => {
      if (isUnassignedId(tag.id)) {
        return <span>{tag.name}</span>
      }

      const shouldShowAsBadge = TAG_TYPES_WITH_BADGE.includes(
        tag.type as TagType,
      )
      const shouldUseSitemapIcon = TAG_TYPES_WITH_SITEMAP_ICON.includes(
        tag.type as TagType,
      )
      const isDraft = tag.id === SitePermitStatus.Draft

      if (shouldShowAsBadge || shouldUseSitemapIcon) {
        const coloredTag = getColoredTag(tag)

        const dataObject = {
          id: coloredTag.id,
          name: coloredTag.name,
          color: coloredTag.color,
          iconName: coloredTag.iconName,
          type: coloredTag.type,
        } as LocationBase

        return (
          <SitemapAttributeTag
            dataObject={dataObject}
            shouldShowAsTag={shouldShowAsBadge && !isDraft}
            isBordered={shouldShowAsBadge && isDraft}
            shouldHideIcon={!shouldUseSitemapIcon}
            className="row y-center"
            iconSize={16}
          >
            <span>{tag.name}</span>
          </SitemapAttributeTag>
        )
      }

      return (
        <>
          {renderCustomIcon(tag)}
          <span>{tag.name}</span>
        </>
      )
    },
    [getColoredTag, renderCustomIcon],
  )

  const renderItem = React.useCallback(
    (tag: FilterItem): React.ReactNode => (
      <div className="row y-center full-width">{renderItemContent(tag)}</div>
    ),
    [renderItemContent],
  )

  const createUnassignedTag = React.useCallback(
    (filterType: FilterType): IFilterTag => ({
      id: getUnassignedId(filterType),
      name: Localization.translator.unassigned,
      type: filterType,
      color: '',
      iconName: null,
    }),
    [],
  )

  const categories =
    React.useMemo((): IDrillDownFilterCategory<FilterItem>[] => {
      return workflowFilterTypes
        .map(filterType => {
          const items = [...getTagsForCategory(filterType)].sort((a, b) =>
            compareLabelsAlphanumeric(a.name, b.name),
          )
          const itemsWithUnassigned: FilterItem[] = [
            ...items,
            createUnassignedTag(filterType),
          ]

          return {
            id: filterType,
            label: getFilterTypeCaption(filterType),
            items: itemsWithUnassigned,
            getItemId: (tag: FilterItem) => tag.id,
            getItemLabel: (tag: FilterItem) => tag.name,
            icon: FILTER_TYPE_ICON_MAP[filterType],
            renderItem,
          }
        })
        .sort((a, b) => compareLabelsAlphanumeric(a.label, b.label))
    }, [getTagsForCategory, renderItem, createUnassignedTag])

  return categories
}
